import dayjs, { Dayjs } from "dayjs";
import { SerialType } from "@utils/types";
import { getFirstOrNull } from "@utils/misc";

import {
  PricingPlan,
  SubscriptionItemType,
  SubscriptionStatus,
} from "@/primary-models";

import {
  ISubscriptionOverview,
  TFreshMealPlan,
  TLitterPlan,
} from "./types/subscriptions.types";
import { getPrettyLitterPlanName, isLitterPlan } from "./types/litter.types";
import { isFreshMealPlan } from "./types/meal-plan.types";
import { isFreshRecipe, TFreshRecipe } from "./types/fresh.types";
import {
  CENTIJAR_UNITS_PER_ITEM,
  FreshExtraItemQuantity,
  FreshExtraProduct,
  isFreshExtraProduct,
  MAX_FRESH_EXTRAS_CAPACITY_PER_BOX,
  TFreshExtraProduct,
} from "./types/extra.types";
import { ICatSubscriptionRecord } from "@api-clients/cats";

const RECIPE_NAME_MAP: Record<SubscriptionItemType, string> = {
  [SubscriptionItemType.CLUCK]: "Cluck!",
  [SubscriptionItemType.GOBBLE]: "Gobble!",
  [SubscriptionItemType.SHREDDED_CLUCK]: "Shredded Cluck!",
  [SubscriptionItemType.SHREDDED_GOBBLE]: "Shredded Gobble!",
  [SubscriptionItemType.QUACK]: "Quack!",
  [SubscriptionItemType.MOOO]: "Mooo!",
  [SubscriptionItemType.OINK]: "Oink!",
  [SubscriptionItemType.SPLASH]: "Splash!",
  [SubscriptionItemType.BAAA]: "Baaa!",
  [SubscriptionItemType.SPRINKLES]: "Sprinkles",
  [SubscriptionItemType.NIBBLES_CHICKEN]: "Nibbles Chicken 50g",
  [SubscriptionItemType.NIBBLES_SALMON]: "Nibbles Salmon 50g",
  [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: "Nibbles Chicken 15g",
  [SubscriptionItemType.NIBBLES_SALMON_POUCH]: "Nibbles Salmon 15g",
  [SubscriptionItemType.NIBBLES_TUNA_POUCH]: "Nibbles Tuna 15g",
  [SubscriptionItemType.SCOOP_HEALTH]: "Health Scoop",
  [SubscriptionItemType.SCOOP_PLANET]: "Planet Scoop",
  [SubscriptionItemType.TOTE_BAG]: "Tote Bag",
};

/** Convert a subscription item type to a pretty product name eg.CLUCK -> Cluck! */
export const getPrettyProductTitle = (
  productTitle: SubscriptionItemType,
): string => RECIPE_NAME_MAP[productTitle] || productTitle;

export const getRecipeNameWithTrackingPrefix = (itemType: SubscriptionItemType): string => {
  const baseName = RECIPE_NAME_MAP[itemType] || itemType;

  if (isFreshRecipe(itemType)) {
    return `2404 ${baseName}`;
  }

  return baseName;
};

export const getAllActiveSubscriptions = (subscriptions: ISubscriptionOverview): ISubscriptionOverview => {
  const freshMealPlans = subscriptions.freshMealPlans.filter(
    (subscription) => (subscription.status === SubscriptionStatus.ACTIVE),
  );

  const litterPlans = subscriptions.litterPlans.filter((subscription) => (
    subscription.status === SubscriptionStatus.ACTIVE
  ));

  return {
    freshMealPlans,
    litterPlans,
  };
};

export const getSubscriptionTitle = (subscription: TFreshMealPlan | TLitterPlan | undefined): string => {
  if (!subscription) {
    return "";
  }
  if (isFreshMealPlan(subscription)) {
    return `${subscription.cat.name}`;
  }
  if (isLitterPlan(subscription)) {
    return getPrettyLitterPlanName(subscription.type);
  }
  return "";
};

type TSubscriptionItemNameAndQuantity = {
  name: SubscriptionItemType;
  quantity: number;
};

export const getCatsFreshRecipesArrayFromSubs = (
  catId: number,
  freshMealPlans: TFreshMealPlan[],
): TSubscriptionItemNameAndQuantity[] => {
  const catSubscription = freshMealPlans.find((s) => s.cat.id === catId);
  return catSubscription?.items?.filter((item) => isFreshRecipe(item.name)).map(({ name, quantity }) => ({
    name: name as TFreshRecipe,
    quantity,
  })) ?? [];
};

export const getCatsExtrasArrayFromSubs = (
  catId: number,
  freshMealPlans: TFreshMealPlan[] | SerialType<TFreshMealPlan>[],
): TSubscriptionItemNameAndQuantity[] => {
  const catSubscription = freshMealPlans.find((s) => s.cat.id === catId);
  return catSubscription?.items?.filter((item) => isFreshExtraProduct(item.name)).map(({ name, quantity }) => ({
    name: name as FreshExtraProduct,
    quantity,
  })) ?? [];
};

export const areExtrasWithinBoxCapacity = (items: FreshExtraItemQuantity, pricingPlan: PricingPlan): boolean => {
  const totalCentiJars = Object.entries(items).reduce((sum, [ key, quantity ]) => {
    if (quantity === undefined) {
      return sum;
    }
    return sum + quantity * CENTIJAR_UNITS_PER_ITEM[key as TFreshExtraProduct];
  }, 0);

  return totalCentiJars <= MAX_FRESH_EXTRAS_CAPACITY_PER_BOX[pricingPlan];
};

export const getMaxQuantityForExtrasProduct = (
  product: FreshExtraProduct,
  currentQuantities: FreshExtraItemQuantity,
  pricingPlan: PricingPlan,
): number => {
  const capacity = MAX_FRESH_EXTRAS_CAPACITY_PER_BOX[pricingPlan];
  const usedByOthers = Object.entries(currentQuantities)
    .filter(([ key ]) => key !== product)
    .reduce(
      (sum, [ key, qty ]) => sum + (qty ?? 0) * CENTIJAR_UNITS_PER_ITEM[key as TFreshExtraProduct],
      0,
    );
  const remaining = capacity - usedByOthers;
  const unitSize = CENTIJAR_UNITS_PER_ITEM[product];
  return Math.max(0, Math.floor(remaining / unitSize));
};

export const getNextDeliveryDateFromSubscription = (subscription: TFreshMealPlan | TLitterPlan): Dayjs | null => {
  const nextDeliveryDate = getNextDeliveryDateForCustomer(subscription);

  if (!nextDeliveryDate) { return null; }
  return dayjs(nextDeliveryDate);
};

export const getNextDeliveryDateFromAllSubscriptions = (
  subscriptions: ISubscriptionOverview | null,
): Dayjs | null => {
  if (!subscriptions) { return null; }
  const sortedDeliveryDates = getSortedSubscriptionDeliveryDates(subscriptions, ESortDirection.ASC);

  return getFirstOrNull(sortedDeliveryDates);
};

export const getDeliveryDatesFromAllSubscriptions = (
  subscriptions: ISubscriptionOverview | null,
): (Dayjs)[] => {
  if (!subscriptions) {
    return [];
  }
  const { freshMealPlans, litterPlans } = getAllActiveSubscriptions(subscriptions);
  return [ ...freshMealPlans, ...litterPlans ].map(
    (subscription) => getNextDeliveryDateFromSubscription(subscription),
  ).filter((date): date is Dayjs => !!date);
};

const getSortedSubscriptionDeliveryDates = (
  subscriptions: ISubscriptionOverview,
  direction: ESortDirection = ESortDirection.ASC,
): Dayjs[] => {
  const deliveryDates = getDeliveryDatesFromAllSubscriptions(subscriptions);
  return getSortedDates(deliveryDates, direction);
};

const getSortedDates = (dates: Dayjs[], direction: ESortDirection = ESortDirection.ASC): Dayjs[] => {
  const sortMultiplier = direction === ESortDirection.ASC ? 1 : -1;
  return dates.sort((a, b) => sortMultiplier *
    (
      (a).unix() -
      (b).unix()
    ));
};

enum ESortDirection { "ASC", "DESC" }
export enum EAccountSubscriptionState {
  ACTIVE,
  INACTIVE,
}

export const getAllFreshMealMealPlansCount = (subscriptions: ISubscriptionOverview | null): number => {
  if (!subscriptions) { return 0; }
  return subscriptions.freshMealPlans.length;
};

export const getAllActiveSubscriptionPlansCount = (subscriptions: ISubscriptionOverview | null)
: number => {
  if (!subscriptions) { return 0; }
  return subscriptions.freshMealPlans.length + subscriptions.litterPlans.length;
};

export const getAccountSubscriptionState = (subscriptions: ISubscriptionOverview | null)
: EAccountSubscriptionState | null => {
  if (!subscriptions) {
    return null;
  }

  const activeSubscriptions = getAllActiveSubscriptions(subscriptions);

  return (getAllActiveSubscriptionPlansCount(activeSubscriptions) > 0 ?
    EAccountSubscriptionState.ACTIVE :
    EAccountSubscriptionState.INACTIVE
  );
};

const getNextChargeDateFromSubscription = (subscription: TFreshMealPlan | TLitterPlan): Dayjs | null => {
  const nextChargeDate = dayjs(subscription.date.nextDelivery).subtract(2, "days");

  if (!nextChargeDate.isValid()) {
    return null;
  }
  return nextChargeDate;
};

const getChargeDatesFromAllSubscriptions = (subscriptions: ISubscriptionOverview): Dayjs[] => {
  const { freshMealPlans, litterPlans } = getAllActiveSubscriptions(subscriptions);
  return [ ...freshMealPlans, ...litterPlans ].map(
    (subscription) => getNextChargeDateFromSubscription(subscription),
  ).filter((date): date is Dayjs => !!date);
};

export const getNextChargeDateFromAllSubscriptions = (
  subscriptions: ISubscriptionOverview | null,
): Dayjs | null => {
  if (!subscriptions) { return null; }
  const sortedChargeDates = getSortedSubscriptionChargeDates(subscriptions, ESortDirection.ASC);

  return getFirstOrNull(sortedChargeDates);
};

export const getAllSubscriptionsByDeliveryDate = (
  subscriptions: ISubscriptionOverview,
  deliveryDate: Dayjs,
): (TFreshMealPlan | TLitterPlan)[] => {
  const freshMealPlans = subscriptions.freshMealPlans.filter(
    (plan) => dayjs(plan.date.nextDelivery).isSame(deliveryDate),
  );
  const litterPlans = subscriptions.litterPlans.filter((plan) => dayjs(plan.date.nextDelivery).isSame(deliveryDate));
  return [ ...freshMealPlans, ...litterPlans ];
};

export const getAllSubscriptionsInNextDelivery = (
  subscriptions: ISubscriptionOverview,
): (TFreshMealPlan | TLitterPlan)[] => {
  const nextDeliveryDate = getNextDeliveryDateFromAllSubscriptions(subscriptions);

  if (!nextDeliveryDate) { return []; }
  return getAllSubscriptionsByDeliveryDate(subscriptions, nextDeliveryDate);
};

/**
 * Returns all active subscriptions whose next scheduled delivery date (nextDelivery, not
 * inProgressDelivery) falls on the earliest upcoming and adjustable delivery date across all subscriptions.
 */
export const getAllSubscriptionsInNextScheduledDelivery = (
  subscriptions: ISubscriptionOverview,
): (TFreshMealPlan | TLitterPlan)[] => {
  const { freshMealPlans, litterPlans } = getAllActiveSubscriptions(subscriptions);

  const dates = [ ...freshMealPlans, ...litterPlans ]
    .map((s) => (s.date.nextDelivery ? dayjs(s.date.nextDelivery) : null))
    .filter((date): date is Dayjs => !!date);

  const sorted = getSortedDates(dates, ESortDirection.ASC);
  const earliestNextDelivery = getFirstOrNull(sorted);

  if (!earliestNextDelivery) { return []; }
  return getAllSubscriptionsByDeliveryDate(subscriptions, earliestNextDelivery);
};

export const getTotalBoxesFulfilled = (
  subscriptions: ISubscriptionOverview | null,
): number => {
  if (!subscriptions) { return 0; }
  const freshMealPlans = subscriptions.freshMealPlans.reduce(
    (total, plan) => total + plan.fulfillment.boxesFulfilled,
    0,
  );
  const litterPlans = subscriptions.litterPlans.reduce(
    (total, plan) => total + plan.fulfillment.boxesFulfilled,
    0,
  );
  return freshMealPlans + litterPlans;
};

export const getAllLegacySubscriptionIdsByDeliveryDate =
  (subscriptions: ISubscriptionOverview, deliveryDate: Dayjs): number[] => {
    const subscriptionsByDeliveryDate = getAllSubscriptionsByDeliveryDate(subscriptions, deliveryDate);
    return subscriptionsByDeliveryDate.map((subscription) => Number(subscription.legacyId));
  };

const getSortedSubscriptionChargeDates = (
  subscriptions: ISubscriptionOverview,
  direction: ESortDirection = ESortDirection.ASC,
): Dayjs[] => {
  const chargeDates = getChargeDatesFromAllSubscriptions(subscriptions);
  return getSortedDates(chargeDates, direction);
};

export interface ISubscriptionCost {
  total: number;
  discounted: number;
  perItem?: number;
  perItemDiscounted?: number;
}

export const getTotalActiveSubscriptionCost = (
  subscriptions: ISubscriptionOverview | null,
)
: ISubscriptionCost | null => {
  if (!subscriptions) {
    return null;
  }
  const activeSubscriptions = getAllActiveSubscriptions(subscriptions);

  if (getAllActiveSubscriptionPlansCount(activeSubscriptions) === 0) {
    return null;
  }

  const { freshMealPlans, litterPlans } = activeSubscriptions;

  const subscriptionCosts = [ ...freshMealPlans, ...litterPlans ].map((subscription) => ({
    total: Number(subscription.pricing.preDiscount.totalCost),
    discounted: Number(subscription.pricing.postDiscount.totalCost),
  }));

  const total = subscriptionCosts.reduce((accumulator, subscriptionCost) => accumulator + (subscriptionCost.total), 0);
  const discounted = subscriptionCosts.reduce(
    (accumulator, subscriptionCost) => accumulator + (subscriptionCost.discounted),
    0,
  );

  return {
    total,
    discounted,
  };

};

/**
 * Checks if a pricing plan is a trial plan (TRIAL or TRIAL_WEEK).
 * @param pricingPlan - The pricing plan to check as a string or enum. Case insensitive.
 * @returns True if the pricing plan is a trial plan, false otherwise.
 */
export const isTrialPlan = (
  pricingPlan?: string | null,
): boolean => {
  if (!pricingPlan) {
    return false;
  }

  const capitalizedPricingPlan = pricingPlan.toUpperCase();

  return capitalizedPricingPlan === PricingPlan.TRIAL || capitalizedPricingPlan === PricingPlan.TRIAL_WEEK;
};

/**
  * Next delivery date doesn't literally map onto the next delivery that the customer will receive.
  * For example, if the inProgressDelivery date is tomorrow, this will be in a month + 1 day's time.
  * Therefore, this function should be used to get the literal next delivery date for a customer.
  */
export const getNextDeliveryDateForCustomer = (subscription: TFreshMealPlan | TLitterPlan)
: string | null => subscription.date.inProgressDelivery ?? subscription.date.nextDelivery;

export const hasTrialBoxOnTheWay = (freshSubscriptions: TFreshMealPlan[]): boolean => freshSubscriptions.some((
  (subscription) => subscription.fulfillment.chargeCount === 1
    && subscription.fulfillment.boxesFulfilled === 0));

export const getActiveCats = (subscriptions: ISubscriptionOverview):
string[] => getAllActiveSubscriptions(subscriptions).freshMealPlans.map((plan) => plan.cat.name);

export const isCatSuppressed = (cat: ICatSubscriptionRecord) => (
  !!cat.details.suppressedAt && !!cat.details.suppressionReason
);

export const getSubscriptionIds = (subscriptionOverview: ISubscriptionOverview | null): number[] => {
  if (!subscriptionOverview) {
    return [];
  }
  const freshIds = subscriptionOverview.freshMealPlans.map((plan) => plan.id);
  const litterIds = subscriptionOverview.litterPlans.map((plan) => plan.id);

  return [
    ...freshIds,
    ...litterIds,
  ];
};
